// Angular imports
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';

// 3rd party imports
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { SpellCheckerModule, SpellCheckerService } from 'ngx-spellchecker';

import {
    DxPopupModule,
    DxFormModule,
    DxAccordionModule,
    DxDataGridModule,
    DxScrollViewModule,
    DxTileViewModule,
    DxLoadPanelModule,
    DxButtonModule,
    DxToolbarModule,
    DxBoxModule,
    DxMultiViewModule,
    DxResponsiveBoxModule,
    DxRadioGroupModule,
    DxTextBoxModule,
    DxValidatorModule,
    DxValidationGroupModule,
    DxFileUploaderModule,
    DxListModule,
    DxSwitchModule,
    DxDropDownButtonModule,
    DxCheckBoxModule,
    DxTextAreaModule,
    DxTabsModule,
    DxDrawerModule,
    DxPopoverModule,
    DxSelectBoxModule,
    DxDropDownBoxModule,
    DxTreeListModule,
    DxDateBoxModule,
    DxMenuModule,
    DxProgressBarModule,
    DxTreeViewModule
} from 'devextreme-angular';

// CC-Common imports
import { CCCommonModule, CCDataGridFeaturesModule } from 'cc-common';
import { AngularSplitModule } from 'angular-split';

// Service imports
import { UserBiographyService } from './_services/user-biography.service';
import { InvoiceDetailsService } from '../cirabooks/shared-cirabooks-module/_services/account-receivable/invoice-details.service';

// Pipe imports
import { CCCurrencyPipe } from '../_pipes/cc-currency.pipe';
import { CCDatePipe } from '../_pipes/cc-date.pipe';
import { CCDateTimePipe } from '../_pipes/cc-datetime.pipe';
import { CCFileIconPipe } from '../_pipes/cc-file-icon.pipe';
import { CCUserTimeZonePipe } from '../_pipes/cc-user-time-zone.pipe';
import { CCNumberPipe } from '../_pipes/cc-number.pipe';
import { PhoneNumberPipe } from '../_pipes/phone-number.pipe';
import { CCItemIconPipe } from '../_pipes/cc-item-icon.pipe';

// Directive imports
import { HasInternalUserColumnsDirective } from './_directives/has-internal-user-columns.directive';
import { HasRestrictedColumnsDirective } from './_directives/has-restricted-columns.directive';

// Component imports
import { UserBiographyComponent } from './user-biography/user-biography.component';
import { UserBioLinkComponent } from './user-bio-link/user-bio-link.component';
import { DirectoryContactCardComponent } from './directory-contact-card/directory-contact-card.component';
import { DirectoryContactCardCompactComponent } from './directory-contact-card-compact/directory-contact-card-compact.component';
import { NotesPopupComponent } from './notes-popup/notes-popup.component';
import { DynamicFormComponent } from './dynamic-form/dynamic-form.component';
import { DynamicDataGridComponent } from './dynamic-data-grid/dynamic-data-grid.component';
import { AttachmentsViewerComponent } from './attachments-viewer/attachments-viewer.component';
import { CommunityOwnerAlertsListComponent } from './community-owner-alerts/community-owner-alerts-list/community-owner-alerts-list.component';
import { CommunityOwnerAlertsEditorComponent } from './community-owner-alerts/community-owner-alerts-editor/community-owner-alerts-editor.component';
import { CommunityOwnerAlertsComponent } from './community-owner-alerts/community-owner-alerts.component';
import { GridActionsComponent } from './grid-actions/grid-actions.component';
import { LiteModePreferenceComponent } from './lite-mode-preference/lite-mode-preference.component';
import { MultiIconButtonTemplateComponent } from './_templates/multi-icon-button-template/multi-icon-button-template.component';
import { CollapsibleFiltersComponent } from './collapsible-filters/collapsible-filters.component';
import { WizardViewerComponent } from './wizard-view/wizard-view.component';
import { HtmlEditorComponent } from './html-editor/html-editor.component';
import { HtmlEditorService } from '../_services/shared-services/html-editor.service';
import { OwnerWelcomeLetterComponent } from './owner-welcome-letter/owner-welcome-letter.component';
import { LogReturnedMailComponent } from './log-returned-mail/log-returned-mail.component';
import { PortfolioAssignmentsComponent } from './portfolio-assignments/portfolio-assignments.component';
import { HtmlEditorLinksComponent } from './html-editor-links/html-editor-links.component';
import { OwnerSelectorComponent } from './owner-selector/owner-selector.component';
import { GridOptionSelectorComponent } from './grid-option-selector/grid-option-selector.component';
import { ManageContactInformationComponent } from './manage-contact-information/manage-contact-information.component';
import { AddContactAddressComponent } from './manage-contact-information/add-contact-address/add-contact-address.component';
import { PropertyCommunityLinkComponent } from './property-community-link/property-community-link.component';
import { SecurityInfoComponent } from './security-info/security-info.component';
import { UserContextTreeComponent } from './user-context-tree/user-context-tree.component';
import { GridActionsMenuComponent } from './grid-actions-menu/grid-actions-menu.component';
import { CustomSelectBoxComponent } from './custom-select-box/custom-select-box.component';
import { UserBioCellTemplateComponent } from './user-bio-cell-template/user-bio-cell-template.component';
import { DynamicCCDataGridComponent } from './dynamic-cc-data-grid/dynamic-cc-data-grid.component';
import { PhotosCarouselViewComponent } from './photos-carousel-view/photos-carousel-view.component';
import { CCIndicatorIconPipe } from '../_pipes/cc-indicator-icon.pipe';
import { RevenueContextTreeComponent } from './revenue-context-tree/revenue-context-tree.component';
import { HyperlinkCreatorComponent } from './hyperlink-creator/hyperlink-creator.component';
import { NotesIconComponent } from './notes-icon/notes-icon.component';
import { FormAttachmentViewerComponent } from './dynamic-form/form-attachment-viewer/form-attachment-viewer.component';
import { FormAttachmentUploaderComponent } from './dynamic-form/form-attachment-uploader/form-attachment-uploader.component';
import { FormPropertyCommunityDocumentsComponent } from './dynamic-form/form-property-community-documents/form-property-community-documents.component';
import { TaskReminderPopoverComponent } from './task-reminder-popover/task-reminder-popover.component';

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        CCCommonModule,
        CCDataGridFeaturesModule,
        AngularSplitModule,
        DxPopupModule,
        DxFormModule,
        DxAccordionModule,
        DxScrollViewModule,
        DxDataGridModule,
        DxTileViewModule,
        DxLoadPanelModule,
        DxButtonModule,
        DxToolbarModule,
        DxBoxModule,
        DxMultiViewModule,
        DxResponsiveBoxModule,
        DxRadioGroupModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxValidationGroupModule,
        DxFileUploaderModule,
        DxListModule,
        DxSwitchModule,
        DxDropDownButtonModule,
        DxCheckBoxModule,
        DxTextAreaModule,
        DxTabsModule,
        DxDrawerModule,
        DxPopoverModule,
        DxSelectBoxModule,
        DxDropDownBoxModule,
        DxTreeListModule,
        DxTreeViewModule,
        DxDateBoxModule,
        DxProgressBarModule,
        EditorModule,
        SpellCheckerModule,
        DxMenuModule
    ],
    declarations: [
        UserBiographyComponent,
        UserBioLinkComponent,
        DirectoryContactCardComponent,
        DirectoryContactCardCompactComponent,
        NotesPopupComponent,
        DynamicFormComponent,
        DynamicDataGridComponent,
        AttachmentsViewerComponent,
        CommunityOwnerAlertsListComponent,
        CommunityOwnerAlertsEditorComponent,
        CommunityOwnerAlertsComponent,
        GridActionsComponent,
        LiteModePreferenceComponent,
        MultiIconButtonTemplateComponent,
        CollapsibleFiltersComponent,
        WizardViewerComponent,
        HtmlEditorComponent,
        CCCurrencyPipe,
        CCNumberPipe,
        CCDatePipe,
        CCDateTimePipe,
        CCFileIconPipe,
        CCUserTimeZonePipe,
        PhoneNumberPipe,
        OwnerWelcomeLetterComponent,
        LogReturnedMailComponent,
        PortfolioAssignmentsComponent,
        HtmlEditorLinksComponent,
        OwnerSelectorComponent,
        GridOptionSelectorComponent,
        ManageContactInformationComponent,
        AddContactAddressComponent,
        PropertyCommunityLinkComponent,
        SecurityInfoComponent,
        UserContextTreeComponent,
        GridActionsMenuComponent,
        CustomSelectBoxComponent,
        UserBioCellTemplateComponent,
        HasInternalUserColumnsDirective,
        HasRestrictedColumnsDirective,
        DynamicCCDataGridComponent,
        PhotosCarouselViewComponent,
        CCIndicatorIconPipe,
        CCItemIconPipe,
        RevenueContextTreeComponent,
        HyperlinkCreatorComponent,
        NotesIconComponent,
        FormAttachmentViewerComponent,
        FormAttachmentUploaderComponent,
        FormPropertyCommunityDocumentsComponent,
        TaskReminderPopoverComponent
    ],
    exports: [
        UserBiographyComponent,
        UserBioLinkComponent,
        DirectoryContactCardComponent,
        DirectoryContactCardCompactComponent,
        DynamicDataGridComponent,
        DynamicCCDataGridComponent,
        DynamicFormComponent,
        CommunityOwnerAlertsComponent,
        GridActionsComponent,
        LiteModePreferenceComponent,
        MultiIconButtonTemplateComponent,
        CollapsibleFiltersComponent,
        WizardViewerComponent,
        HtmlEditorComponent,
        CCCurrencyPipe,
        CCDatePipe,
        CCDateTimePipe,
        CCFileIconPipe,
        CCUserTimeZonePipe,
        CCNumberPipe,
        PhoneNumberPipe,
        OwnerWelcomeLetterComponent,
        LogReturnedMailComponent,
        HtmlEditorLinksComponent,
        OwnerSelectorComponent,
        GridOptionSelectorComponent,
        ManageContactInformationComponent,
        PropertyCommunityLinkComponent,
        SecurityInfoComponent,
        UserContextTreeComponent,
        GridActionsMenuComponent,
        CustomSelectBoxComponent,
        UserBioCellTemplateComponent,
        HasInternalUserColumnsDirective,
        HasRestrictedColumnsDirective,
        PhotosCarouselViewComponent,
        CCIndicatorIconPipe,
        CCItemIconPipe,
        RevenueContextTreeComponent,
        HyperlinkCreatorComponent,
        NotesIconComponent,
        TaskReminderPopoverComponent
    ],
    providers: [
        UserBiographyService,
        InvoiceDetailsService,
        SpellCheckerService,
        HtmlEditorService,
        { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' }
    ]
})
export class SharedModule {}
